"use client";

import MediaImage from "@/components/ui/MediaImage";

import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { CaseData, ImageData } from "@/cms/models";
import {
  getCaseGalleryImages,
  type CaseGalleryMedia,
  type CaseGalleryViewer,
} from "@/utils/case-gallery";
import AiMarkingBadge from "./AiMarkingBadge";
import { RealequityVideoComponent } from "./RealequityVideoComponent";
import { swipeStep } from "@/utils/swipe";
import { useHistoryEntry } from "@/hooks/useHistoryEntry";

export type CaseMediaViewerItem =
  | { type: "image"; image: ImageData }
  | { type: "video"; url: string };

interface CaseMediaViewerProps {
  caseData: Pick<
    CaseData,
    | "images"
    | "imageUrl"
    | "videoUrls"
    | "advertisementAddressOrDisplayName"
    | "shortAddress"
  >;
  variant: CaseGalleryViewer;
  view: CaseGalleryMedia;
  initialIndex?: number;
  /** A mixed list in the caller's order; replaces the list `view` selects. */
  media?: CaseMediaViewerItem[];
  /** The slideshow also changes photo on a horizontal swipe; off on the case pages. */
  swipe?: boolean;
  onClose: () => void;
}

export default function CaseMediaViewer({
  caseData,
  variant,
  view,
  initialIndex = 0,
  media,
  swipe = false,
  onClose,
}: CaseMediaViewerProps) {
  const entries: CaseMediaViewerItem[] =
    media ??
    (view === "video"
      ? (caseData.videoUrls ?? []).map((url) => ({ type: "video", url }))
      : getCaseGalleryImages(caseData)
          .filter((image) => image.namespace.endsWith(view))
          .map((image) => ({ type: "image", image })));
  const count = entries.length;
  const noun = !media && view === "drawing" ? "plantegning" : "billede";
  const [index, setIndex] = useState(
    Math.min(initialIndex, Math.max(0, count - 1)),
  );
  const [zoom, setZoom] = useState(false);
  const [slideshow, setSlideshow] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const initialImage = useRef<HTMLDivElement>(null);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const change = useCallback(
    (direction: number) => {
      setIndex((current) => (current + direction + count) % Math.max(1, count));
      setZoom(false);
    },
    [count],
  );
  useEffect(() => {
    if (variant !== "slideshow") return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
        event.preventDefault();
        change(event.key === "ArrowRight" ? 1 : -1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [variant, change]);
  useEffect(() => {
    if (!slideshow || count < 2) return;
    const timer = window.setInterval(() => change(1), 5000);
    return () => window.clearInterval(timer);
  }, [slideshow, count, change]);
  useEffect(() => {
    if (variant === "scroll" && initialIndex > 0)
      initialImage.current?.scrollIntoView({ block: "start" });
  }, [variant, initialIndex]);
  const close = () => {
    if (document.fullscreenElement === panel.current)
      document.exitFullscreen().catch(() => {});
    onClose();
  };
  useHistoryEntry(close);
  const title = `${caseData.advertisementAddressOrDisplayName} – ${media ? "billeder" : view === "drawing" ? "plantegninger" : view === "video" ? "video" : "billeder"}`;
  const current = entries[index];

  return (
    <Dialog
      open
      onClose={close}
      className={
        variant === "slideshow"
          ? "re-cinematic-viewer"
          : "fixed inset-0 z-[110] bg-[var(--gallery-bg-color,white)] text-[var(--gallery-text-color,inherit)]"
      }
      data-case-gallery-viewer={variant}
    >
      <DialogPanel
        ref={panel}
        className={
          variant === "slideshow"
            ? "re-cinematic-viewer-panel"
            : "h-dvh overflow-auto"
        }
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {variant === "scroll" ? (
          <>
            <button
              type="button"
              aria-label="Luk galleri"
              onClick={close}
              className="fixed right-4 top-4 z-50 flex size-10 items-center justify-center rounded-full border bg-[var(--gallery-bg-color,white)] text-2xl"
            >
              ×
            </button>
            <div className="flex min-h-full flex-col gap-1">
              {entries.map((entry, position) => {
                if (entry.type === "video")
                  return media ? (
                    <div
                      key={`${entry.url}-${position}`}
                      ref={position === initialIndex ? initialImage : undefined}
                    >
                      <RealequityVideoComponent
                        url={entry.url}
                        controls
                        className="max-h-[90vh] w-full"
                      />
                    </div>
                  ) : (
                    <RealequityVideoComponent
                      key={entry.url}
                      url={entry.url}
                      controls
                      className="max-h-[90vh] w-full"
                    />
                  );
                const image = entry.image;
                return (
                  <div
                    key={`${image.lgUrl}-${position}`}
                    ref={position === initialIndex ? initialImage : undefined}
                    className="relative flex w-full justify-center"
                  >
                    <div className="relative">
                      {image.description && (
                        <div className="homepage-text-sm absolute left-0 top-0 z-10 bg-[var(--gallery-bg-color,white)] p-2">
                          {image.description}
                        </div>
                      )}
                      <picture>
                        <source
                          media="(min-width: 640px)"
                          srcSet={encodeURI(image.xlUrl)}
                        />
                        <MediaImage
                          src={encodeURI(image.lgUrl)}
                          alt={
                            image.description ||
                            `${caseData.shortAddress}, billede ${position + 1}`
                          }
                          className="max-h-[95vh] max-w-full object-contain"
                          loading={
                            Math.abs(position - initialIndex) < 2
                              ? "eager"
                              : "lazy"
                          }
                          onLoad={() => {
                            if (position === initialIndex && initialIndex > 0)
                              initialImage.current?.scrollIntoView({
                                block: "start",
                              });
                          }}
                        />
                      </picture>
                      <AiMarkingBadge image={image} />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <div className="re-cinematic-viewer-bar">
              <span aria-live="polite">
                {count ? index + 1 : 0} / {count}
              </span>
              <div>
                {current?.type !== "video" && (media || view !== "video") && (
                  <>
                    <button
                      type="button"
                      aria-label="Zoom"
                      aria-pressed={zoom}
                      onClick={() => setZoom((current) => !current)}
                    >
                      ⊕
                    </button>
                    <button
                      type="button"
                      aria-label={
                        slideshow ? "Stop slideshow" : "Start slideshow"
                      }
                      aria-pressed={slideshow}
                      onClick={() => setSlideshow((current) => !current)}
                    >
                      {slideshow ? "Ⅱ" : "▷"}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  aria-label="Fuld skærm"
                  onClick={() => {
                    if (document.fullscreenElement)
                      document.exitFullscreen().catch(() => {});
                    else panel.current?.requestFullscreen?.().catch(() => {});
                  }}
                >
                  ⛶
                </button>
                <button
                  type="button"
                  aria-label="Luk galleri"
                  onClick={close}
                  autoFocus
                >
                  ×
                </button>
              </div>
            </div>
            <div
              className="re-cinematic-slides"
              {...(swipe && count > 1
                ? {
                    style: { touchAction: "pan-y" },
                    onPointerDown: (event: ReactPointerEvent) => {
                      swipeStart.current = {
                        x: event.clientX,
                        y: event.clientY,
                      };
                    },
                    onPointerUp: (event: ReactPointerEvent) => {
                      const start = swipeStart.current;
                      swipeStart.current = null;
                      if (!start || zoom) return;
                      const direction = swipeStep(
                        event.clientX - start.x,
                        event.clientY - start.y,
                      );
                      if (direction) change(direction);
                    },
                    onPointerCancel: () => {
                      swipeStart.current = null;
                    },
                    // A mouse drag would otherwise drag the picture and cancel the pointer.
                    onDragStart: (event: ReactDragEvent) =>
                      event.preventDefault(),
                  }
                : {})}
            >
              {current?.type === "video" ? (
                <video
                  key={current.url}
                  src={current.url}
                  controls
                  className="h-full w-full"
                />
              ) : (
                current && (
                  <>
                    <MediaImage
                      className={zoom ? "is-zoomed" : ""}
                      src={current.image.xlUrl || current.image.lgUrl}
                      alt={
                        current.image.description ||
                        `${caseData.shortAddress}, ${noun} ${index + 1}`
                      }
                    />
                    <AiMarkingBadge image={current.image} />
                  </>
                )
              )}
              {count > 1 && (
                <>
                  <button
                    type="button"
                    className="re-cinematic-prev"
                    aria-label="Forrige"
                    onClick={() => change(-1)}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="re-cinematic-next"
                    aria-label="Næste"
                    onClick={() => change(1)}
                  >
                    ›
                  </button>
                </>
              )}
            </div>
            {(media || view !== "video") && (
              <div className="re-cinematic-thumbnails">
                {entries.map((entry, position) => (
                  <button
                    key={`${entry.type === "video" ? entry.url : entry.image.mdUrl}-${position}`}
                    type="button"
                    aria-label={`Vis ${entry.type === "video" ? "video" : noun} ${position + 1}`}
                    aria-current={index === position}
                    onClick={() => {
                      setIndex(position);
                      setZoom(false);
                    }}
                  >
                    {entry.type === "video" ? (
                      <span aria-hidden>▶</span>
                    ) : (
                      <MediaImage
                        src={entry.image.mdUrl || entry.image.lgUrl}
                        alt=""
                        loading="lazy"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </DialogPanel>
    </Dialog>
  );
}
